import { useEffect, useState } from "react";
import { loadInvoicePdfBlob } from "@/lib/invoicePdf";

export interface InvoiceDraftPdfPreviewProps {
  invoiceId: string;
  /** Wechselt, wenn Positionen oder Anschrift neu vom Server kommen. */
  revision: string;
  staleHint?: string | null;
}

export function InvoiceDraftPdfPreview({
  invoiceId,
  revision,
  staleHint,
}: InvoiceDraftPdfPreviewProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);

    loadInvoicePdfBlob(invoiceId, { withLetterhead: true })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setBlobUrl(null);
        setError(e instanceof Error ? e.message : "Vorschau konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [invoiceId, revision]);

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-label">Vorschau der Rechnung</h3>
        <p className="text-xs text-label-tertiary">
          So sieht das PDF nach dem Ausstellen aus. Das Wasserzeichen entfällt dann.
        </p>
      </div>
      {staleHint ? <p className="text-xs text-status-orange">{staleHint}</p> : null}
      <div className="overflow-hidden rounded-xl border border-separator bg-white">
        {loading && !blobUrl ? (
          <p className="px-4 py-16 text-center text-sm text-neutral-500">Vorschau wird erzeugt…</p>
        ) : null}
        {error ? (
          <p className="px-4 py-8 text-center text-sm text-status-red">{error}</p>
        ) : null}
        {blobUrl ? (
          <iframe
            title="Vorschau der Rechnung mit Wasserzeichen Entwurf"
            src={blobUrl}
            className="h-[min(70vh,42rem)] w-full bg-white"
          />
        ) : null}
      </div>
      {loading && blobUrl ? (
        <p className="text-xs text-label-tertiary">Vorschau wird aktualisiert…</p>
      ) : null}
    </section>
  );
}
